import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { BookTemplate, Plus, Pencil, Trash2, Package, X, Download } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = [
  { value: "altro", label: "Altro" },
  { value: "mobili", label: "Mobili" },
  { value: "elettrodomestici", label: "Elettrodomestici" },
  { value: "scatole", label: "Scatole" },
];

const emptyT = { name: "", materials: "", items: [], notes: "" };

export default function JobTemplatesManager({ pickOnly = false, onPick = null }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyT);
  const [newItem, setNewItem] = useState({ name: "", quantity: 1, category: "altro" });

  const load = async () => {
    try { const { data } = await api.get("/job-templates"); setList(data); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(emptyT); setOpen(true); };
  const openEdit = (t) => {
    setEditing(t);
    setForm({ name: t.name, materials: t.materials || "", items: t.items || [], notes: t.notes || "" });
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Dai un nome al modello");
    try {
      if (editing) {
        await api.put(`/job-templates/${editing.id}`, form);
        toast.success("Modello aggiornato");
      } else {
        await api.post("/job-templates", form);
        toast.success("Modello creato");
      }
      setOpen(false);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const remove = async (t) => {
    if (!confirm(`Eliminare il modello "${t.name}"?`)) return;
    try { await api.delete(`/job-templates/${t.id}`); toast.success("Eliminato"); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const addItem = () => {
    if (!newItem.name.trim()) return;
    setForm({ ...form, items: [...form.items, { ...newItem, quantity: Number(newItem.quantity) || 1 }] });
    setNewItem({ name: "", quantity: 1, category: "altro" });
  };

  return (
    <section data-testid="job-templates-section">
      <div className="flex items-center gap-2 mb-3">
        <BookTemplate className="w-4 h-4 text-indigo-600" />
        <h3 className="font-display font-bold text-slate-900">Modelli Lavoro Rapidi</h3>
        {isAdmin && !pickOnly && (
          <Button type="button" size="sm" onClick={openCreate} className="ml-auto bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="create-template-button">
            <Plus className="w-4 h-4 mr-1" /> Nuovo
          </Button>
        )}
      </div>
      {list.length === 0 ? (
        <div className="text-xs text-slate-400 italic bg-slate-50 border border-dashed border-slate-200 rounded-xl p-4 text-center">
          Nessun modello salvato. {isAdmin && "Crea i tuoi template preferiti (es. 'Bilocale', 'Villa 4 locali', 'Ufficio')."}
        </div>
      ) : (
        <div className="grid gap-2 grid-cols-2 sm:grid-cols-3">
          {list.map((t) => (
            <div key={t.id} className="bg-white border border-slate-200 rounded-xl p-3 hover:border-indigo-400 transition" data-testid={`template-card-${t.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-semibold text-slate-900 truncate">{t.name}</div>
                  <div className="text-[10px] text-slate-400 font-mono mt-0.5">
                    {t.items?.length || 0} oggett{t.items?.length === 1 ? "o" : "i"}
                  </div>
                </div>
                {isAdmin && !pickOnly && (
                  <div className="flex gap-0.5">
                    <button onClick={() => openEdit(t)} className="p-1 text-slate-400 hover:text-indigo-600" data-testid={`edit-template-${t.id}`}><Pencil className="w-3.5 h-3.5" /></button>
                    <button onClick={() => remove(t)} className="p-1 text-slate-400 hover:text-rose-600" data-testid={`delete-template-${t.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                )}
              </div>
              {onPick && (
                <button
                  type="button"
                  onClick={() => onPick(t)}
                  className="mt-2 w-full bg-indigo-50 hover:bg-indigo-100 text-indigo-700 text-xs font-bold rounded-lg py-1.5 border border-indigo-200 inline-flex items-center justify-center gap-1"
                  data-testid={`apply-template-${t.id}`}
                >
                  <Download className="w-3 h-3" /> Usa questo
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Editor dialog */}
      <Dialog open={open} onOpenChange={(o) => !o && setOpen(false)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? "Modifica Modello" : "Nuovo Modello"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div>
              <Label>Nome Modello</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required placeholder="Es: Bilocale, Villa 4 locali, Ufficio" data-testid="template-name-input" />
            </div>
            <div>
              <Label>Materiali</Label>
              <Textarea rows={2} value={form.materials} onChange={(e) => setForm({ ...form, materials: e.target.value })} placeholder="Es: 20 scatoloni, nastro, pluriball, 2 cinghie" data-testid="template-materials-input" />
            </div>
            <div>
              <Label>Oggetti</Label>
              <div className="space-y-1.5 mt-1">
                {form.items.map((it, idx) => (
                  <div key={idx} className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-lg px-2 py-1.5 text-sm">
                    <Package className="w-3.5 h-3.5 text-indigo-500" />
                    <span className="font-mono text-xs text-indigo-700 font-bold">×{it.quantity}</span>
                    <span className="flex-1">{it.name}</span>
                    <span className="text-[10px] uppercase bg-slate-200 rounded px-1.5">{it.category}</span>
                    <button type="button" onClick={() => setForm({ ...form, items: form.items.filter((_, i) => i !== idx) })} className="text-slate-400 hover:text-rose-600"><X className="w-3.5 h-3.5" /></button>
                  </div>
                ))}
              </div>
              <div className="flex gap-2 mt-2 bg-slate-50 border border-dashed border-slate-300 rounded-lg p-2">
                <Input placeholder="Oggetto" value={newItem.name} onChange={(e) => setNewItem({ ...newItem, name: e.target.value })} className="flex-1" />
                <Input type="number" min="1" value={newItem.quantity} onChange={(e) => setNewItem({ ...newItem, quantity: e.target.value })} className="w-16" />
                <select value={newItem.category} onChange={(e) => setNewItem({ ...newItem, category: e.target.value })} className="border border-slate-200 rounded-md px-2 text-sm">
                  {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
                <Button type="button" variant="outline" size="sm" onClick={addItem}><Plus className="w-4 h-4" /></Button>
              </div>
            </div>
            <div>
              <Label>Note aggiuntive (opz.)</Label>
              <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
              <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="save-template-button">Salva</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </section>
  );
}
